/**
 * AdminManualKev — Admin panel section for managing "Internal Advisories".
 *
 * Lists every manual KEV entry the DB has (across all admins), lets the
 * *creating* admin edit or delete via the shared ManualKevModal. Non-owner
 * admins see the entry but their action buttons are disabled with a tooltip.
 * Also exposes an "Add advisory" CTA at the top-right.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import ManualKevModal from "./ManualKevModal";

export default function AdminManualKev() {
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, listRes] = await Promise.all([
        api.get("/auth/me").catch(() => ({ data: null })),
        api.get("/kev/manual"),
      ]);
      setMe(meRes.data);
      setItems(listRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load advisories");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (it) => {
    if (!window.confirm(`Delete internal advisory ${it.cve}?`)) return;
    try {
      await api.delete(`/kev/manual/${it.id}`);
      toast.success("Advisory deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-6" data-testid="admin-manual-kev">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-600" />
            Manual KEV · Internal Advisories
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Publish custom exploited-vulnerability entries alongside the CISA feed. Only the admin who added an entry can edit or delete it.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true); }}
          data-testid="admin-manual-kev-add"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-[#2E7DF5] text-white hover:bg-[#1e6fdb]"
        >
          <Plus className="w-3.5 h-3.5" /> Add advisory
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading advisories…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg py-10 text-center text-sm text-slate-500" data-testid="admin-manual-kev-empty">
          No internal advisories yet. Click <strong>Add advisory</strong> to publish the first one.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
          {items.map((it, i) => {
            const mine = me?.id && it.created_by && it.created_by === me.id;
            return (
              <div key={it.id || i} data-testid={`admin-manual-row-${i}`} className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 hover:bg-slate-50">
                <a href={it.detail_url || it.nvd_url || "#"} target="_blank" rel="noopener noreferrer" className="font-mono text-xs font-medium text-[#2E7DF5] w-32 shrink-0 hover:underline flex items-center gap-1">
                  {it.cve}
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </a>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{it.name || <span className="italic text-slate-400">(no name)</span>}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {[it.vendor, it.product].filter(Boolean).join(" · ") || <span className="italic text-slate-400">(no vendor/product)</span>}
                    {it.created_by_email ? <span className="ml-1.5 text-slate-400">· added by {it.created_by_email}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {it.known_ransomware_use && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700">Ransomware</span>
                  )}
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Manual</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditing(it); setModalOpen(true); }}
                    disabled={!mine}
                    title={mine ? "Edit" : "Only the admin who added this can edit"}
                    data-testid={`admin-manual-edit-${i}`}
                    className={`p-1.5 rounded transition-colors ${mine ? "text-slate-500 hover:text-[#2E7DF5] hover:bg-slate-100" : "text-slate-300 cursor-not-allowed"}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(it)}
                    disabled={!mine}
                    title={mine ? "Delete" : "Only the admin who added this can delete"}
                    data-testid={`admin-manual-del-${i}`}
                    className={`p-1.5 rounded transition-colors ${mine ? "text-slate-500 hover:text-red-600 hover:bg-slate-100" : "text-slate-300 cursor-not-allowed"}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ManualKevModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={load}
        initialEntry={editing}
      />
    </div>
  );
}
